/** angular */
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable } from 'rxjs';
/** end angular */

/** services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** end services */

/** models */
import { DsPinsFilters } from '../models/ds-pins-filters';
import { DsPin } from '../models/ds-pin';
/** end models */

/** interfaces */
import { DsPinInterface } from '../interfaces/ds-pin.interface';
/** end interfaces */

/** functions */
import { BydGenerateParams } from '../../../functions/byd-generate-params.function';
/** end functions */

/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
import { DsPinAddDataInterface } from '../interfaces/ds-pin-add-data.interface';
import { DsPinUpdateDto } from '../models/ds-pin-update-dto.model';

@Injectable({
  providedIn: 'root',
})
export class DsPinsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get filtered pins list.
   *
   * @param projectId Project ID
   * @param numberPerPage Number of results per page
   * @param queries collection of matrix and query URL parameters
   * @param pageIndex Current page index of results
   * @returns Observable type `<BydModel<DsPin>>`
   */
  public getFilteredPinsList(
    projectId: number,
    queries: Params,
    numberPerPage: number,
    pageIndex: number
  ): Observable<BydModel<DsPin>> {
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(numberPerPage, pageIndex);
    const queryParams = new DsPinsFilters(queries);
    const params = BydGenerateParams<DsPinsFilters>(queryParams);

    this.path = `/dspin/v1/projects/${projectId}/pins`;
    return this.getModels<DsPin>({ headers, params });
  }

  /**
   * To read/download a pin photo by its uuid.
   *
   * @param projectId The Project ID
   * @param pinId The pin ID
   * @param pinPhotoUuid The pin photo uuid
   * @returns Observable<Blob>
   */
  public getPinPhotoStream(projectId: number, pinId: number, pinPhotoUuid: string): Observable<Blob> {
    this.path = `/v1/dspin/projects/${projectId}/pins/${pinId}/photos/${pinPhotoUuid}`;
    return this.get<Blob>({
      responseType: 'blob',
    });
  }

  /**
   * Update a pin.
   *
   * @param projectId Current project ID
   * @param pinId ID of the pin to edit
   * @param pin The pin data needed to update a pin
   * @returns Observable<DsPin>
   */
  public updatePin(projectId: number, pinId: number, pin: DsPinUpdateDto): Observable<DsPin> {
    this.path = `/v1/dspin/projects/${projectId}/pins/${pinId}`;
    return this.putModel<DsPin>({ body: pin });
  }

  /**
   * Get blob of the pin position's photo.
   *
   * @param projectId Project ID
   * @param pinId ID of the pin
   * @returns Blob (the file stream)
   */
  public getPinPositionPhoto(projectId: number, pinId: number): Observable<Blob> {
    this.path = `/v1/projects/${projectId}/pins/${pinId}/positions/photos`;
    return this.get({ responseType: 'blob' });
  }

  /**
   * Get pin.
   *
   * @param projectId Project ID
   * @param pinId ID of the pin
   * @returns Observable<DsPin>
   */
  public getPin(projectId: number, pinId: number): Observable<DsPin> {
    this.path = `/v1/projects/${projectId}/pins/${pinId}`;
    return this.getModel<DsPin>();
  }

  /**
   * Add a new pin.
   *
   * @param projectId the project id
   * @param data The pin data needed to create a new pin
   * @returns Observable<DsPin>
   */
  public addPin(projectId: number, data: DsPinAddDataInterface): Observable<DsPin> {
    this.path = `/v1/dspin/projects/${projectId}/pins`;
    return this.postModel<DsPin>({ body: data });
  }

  /**
   * Upload pin photo.
   *
   * @param projectId Project ID
   * @param pinId ID of the pin
   * @param file New pin photo
   * @returns Observable<boolean>
   */
  public uploadPinPhoto(projectId: number, pinId: number, file: File): Observable<string> {
    this.path = `/v1/dspin/projects/${projectId}/pins/${pinId}/photos`;
    const formData = new FormData();
    formData.append('file', file);

    const headers = this.bydConfigService.headers.delete('Content-Type');
    return this.post<string>({ body: formData, headers, responseType: 'text' });
  }

  /**
   * Delete pin photo.
   *
   * @param projectId Project ID
   * @param pinId ID of the pin
   * @param pinPhotoId The pin photo uuid
   * @returns Observable<boolean>
   */
  public deletePinPhoto(projectId: number, pinId: number, pinPhotoId: string): Observable<boolean> {
    this.path = `/v1/dspin/projects/${projectId}/pins/${pinId}/photos/${pinPhotoId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsPin instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsPin` instance.
   */
  public createNewInstance(jsonObject: DsPinInterface): DsPin {
    return new DsPin(jsonObject);
  }
}
